import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useStoreActions, useStoreState } from 'easy-peasy';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { pick } from 'lodash';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import _e from 'common/localeUtil';
import EditorLeftPanelWebsite from './EditorLeftPanelWebsite';
import EditorLeftPanelReport from './EditorLeftPanelReport';
import ShowMessageDialog from './dialog/ShowMessageDialog';
import { MESSAGE_TYPES, publishTemplate, setContactFormToTemplate, changeTemplate } from '../utils';
import SaveSuccessfulDialog from './dialog/SaveSuccessfulDialog';
import GMButton from './button/GMButton';
import { REVISION_STATUSES } from '../constants';

export const contactFormYupSchema = Yup.object({
	receiving_email: Yup.string().label('Email').email().required(),
	mobile_number: Yup.string().label('Phone number').nullable().notRequired(),
	facebook: Yup.string().label('Facebook').nullable().notRequired(),
	twitter: Yup.string().label('Twitter').nullable().notRequired(),
	instagram: Yup.string().label('Instagram').nullable().notRequired(),
	linkedin: Yup.string().label('Linkedin').nullable().notRequired(),
	whatsapp: Yup.string().label('Whatsapp').nullable().notRequired(),
	telegram: Yup.string().label('Telegram').nullable().notRequired(),
});

const websiteFormYupSchema = Yup.object({
	subdomain: Yup.string()
		.matches(
			/^[a-zA-Z0-9_-]+$/,
			'Domain can only contain alphanumeric, underscore and dash characters',
		)
		.label('Domain')
		.required(),
	contactForm: contactFormYupSchema,
	seo: Yup.object({
		seo_title: Yup.string().label(_e('title')).nullable().notRequired(),
		seo_description: Yup.string().label(_e('description')).nullable().notRequired(),
		seo_focus_keyword: Yup.string().label(_e('keywords')).nullable().notRequired(),
		seo_image_url: Yup.string().nullable().label('Snippet image').notRequired(),
	}),
	extraProfile: Yup.object({
		accolades: Yup.array()
			.of(
				Yup.object({
					title: Yup.string().label(_e('title')).nullable().notRequired(),
					description: Yup.string().label(_e('description')).nullable().notRequired(),
				}).notRequired(),
			)
			.label('Accolades')
			.notRequired(),
		services: Yup.array()
			.of(
				Yup.object({
					title: Yup.string().label(_e('title')).nullable().notRequired(),
					description: Yup.string().label(_e('description')).nullable().notRequired(),
				}).notRequired(),
			)
			.label('Services')
			.notRequired(),
		story: Yup.string().label(_e('story')).nullable().notRequired(),
		profile_picture: Yup.string().nullable().label(_e('avatar')).notRequired(),
	}).notRequired(),
});

const EditorLeftPanel = ({ toggleDrawer }) => {
	const { template, contactForm, seo, subdomain, adviserData, extraProfile } = useStoreState(
		(state) => state.template,
	);
	if (!window.selectedTemplate) {
		window.selectedTemplate = template;
	}

	const showingMessage = useStoreState((state) => state.showMessage.showingMessage);
	const showMessage = useStoreActions((actions) => actions.showMessage.showMessage);
	const saveAllSettings = useStoreActions((actions) => actions.template.saveAllSettings);
	const loadTemplate = useStoreActions((actions) => actions.template.loadTemplate);
	const setEbook = useStoreActions((actions) => actions.template.setEbook);
	const [showSaveSuccessfulDialog, setShowSaveSuccessfulDialog] = useState();

	const toggleShowSaveSuccessfulDialog = () => {
		setShowSaveSuccessfulDialog(!showSaveSuccessfulDialog);
	};

	const handleExitFullScreen = () => {
		window.grapesjs.stopCommand('core:preview');
	};

	const storeTemplate = (type, showDialog) =>
		new Promise((resolve) => {
			window.grapesjs.store(async (res) => {
				console.log('Store callback');
				try {
					if (res) {
						if (type === REVISION_STATUSES.DRAFT && showDialog) {
							setShowSaveSuccessfulDialog(true);
						}
						if (type === REVISION_STATUSES.PUBLISHED) {
							setEbook({
								final_s3_url: 'Generating the final eBook PDF, please wait ...',
							});
							const publishResult = await publishTemplate(template);
							console.log('publishResult', publishResult);
							setEbook({ final_s3_url: publishResult.final_s3_url });
						}
					} else {
						showMessage({ text: 'Saved error !!!', type: MESSAGE_TYPES.FAIL });
					}
					await loadTemplate();
				} finally {
					resolve(Boolean(res));
				}
			});
		});

	const saveAll = async (values, type, beforeStore, showDialog = true) => {
		window.gm_track(`Save button clicked`, {
			brandingType: window.GM_BRANDING_TYPE,
			values,
			type,
		});

		if (!window.GM_BRANDING_TYPE_REPORT_EBOOK) {
			const response = await saveAllSettings(values);
			console.log('saveAll response', response);
			const responseStatus = response?.status;
			if (responseStatus !== 200) {
				const error = response.originalError?.response?.data?.error;
				console.log('saveAll error', error);
				if (error) {
					showMessage({ text: error, type: MESSAGE_TYPES.FAIL });
				} else {
					showMessage({ text: 'Saved error !!!', type: MESSAGE_TYPES.FAIL });
				}
				return false;
			}
			setContactFormToTemplate(values.contact_form);
		}

		if (beforeStore) {
			await beforeStore();
		}

		return storeTemplate(type, showDialog);
	};

	const seoInitialValues = pick(seo, [
		'seo_title',
		'seo_description',
		'seo_focus_keyword',
		'seo_image_url',
		'seo_favicon_url',
	]);

	const contact = {
		...contactForm,
		phoneNumber: adviserData.mobile_number,
	};
	return (
		<>
			<Formik
				initialValues={{
					adviserId: adviserData.id,
					subdomain,
					contactForm: contact,
					seo: { ...seoInitialValues },
					extraProfile: {
						accolades: extraProfile.accolades || [
							{
								title: '',
								description: '',
							},
						],
						services: extraProfile.services || [
							{
								title: '',
								description: '',
							},
						],
						story: extraProfile.story || '',
						profile_picture:
							extraProfile.profile_picture || adviserData.profile_picture || '',
					},
					submissionSource: 'configPanel',
					primaryColor: '#000000',
				}}
				validationSchema={websiteFormYupSchema}
				onSubmit={async (values, { setSubmitting }) => {
					const { seo, subdomain, extraProfile, contactForm, revisionStatus } = values;
					const submitValues = {
						seo,
						subdomain: subdomain.toLowerCase(),
						extra_profile: extraProfile,
					};
					const isFromWizard = values.submissionSource === 'welcomeWizard';
					if (isFromWizard) {
						submitValues.contact_form = contactForm;
					}
					// The wizard values must be applied before GrapesJS stores the template.
					// Otherwise only extra_profile is persisted and a refresh restores the old HTML.
					const applyWizardValues = isFromWizard
						? async () => {
								const newAdviserInfo = {
									adviserData,
									contactForm,
									extraProfile,
								};
								console.log('saveAll changeTemplate', { ...newAdviserInfo });
								await changeTemplate(
									template,
									{ forceReset: true },
									newAdviserInfo,
								);
							}
						: undefined;

					await saveAll(submitValues, revisionStatus, applyWizardValues, !isFromWizard);
					console.log('saveAll onSubmit', {
						isFromWizard,
						submitValues,
						values,
					});
					setSubmitting(false);
				}}
			>
				{(formikBags) => (
					<Form noValidate>
						{!window.GM_BRANDING_TYPE_REPORT_EBOOK ? (
							<EditorLeftPanelWebsite {...formikBags} toggleDrawer={toggleDrawer} />
						) : (
							<EditorLeftPanelReport {...formikBags} toggleDrawer={toggleDrawer} />
						)}
						<div className={`exit-fullscreen-button`}>
							<GMButton
								Icon={FullscreenExitIcon}
								title={`Back to builder`}
								onClick={handleExitFullScreen}
							/>
						</div>
					</Form>
				)}
			</Formik>
			{showingMessage && <ShowMessageDialog open={showingMessage} />}
			{showSaveSuccessfulDialog && (
				<SaveSuccessfulDialog
					open={showSaveSuccessfulDialog}
					handleClose={toggleShowSaveSuccessfulDialog}
				/>
			)}
		</>
	);
};

EditorLeftPanel.propTypes = {
	toggleDrawer: PropTypes.func,
	editor: PropTypes.object,
	changeBackgroundDialogOpen: PropTypes.bool,
};

export default EditorLeftPanel;
